import type { Locale } from "./getLocale";

// PASTE #233 path-based ES: helpers for the /es/<route> Spanish URL
// prefix. Public Spanish pages live at /es/... (a real crawlable URL);
// middleware rewrites them to the bare page handler + tags the request
// x-mag-locale=es so getLocale() renders Spanish. These helpers keep the
// prefix math in one place for the rewrite, canonical/hreflang, sitemap,
// and locale-aware internal links.

export const ES_PREFIX = "/es";

// "/" -> "/es" ; "/catalog" -> "/es/catalog" ; "/catalog?q=x" -> "/es/catalog?q=x"
export function esPath(path: string): string {
  return path === "/" ? ES_PREFIX : `${ES_PREFIX}${path}`;
}

// "/es" -> "/" ; "/es/catalog" -> "/catalog" ; anything else unchanged
export function stripEsPrefix(path: string): string {
  if (path === ES_PREFIX) return "/";
  if (path.startsWith(`${ES_PREFIX}/`)) return path.slice(ES_PREFIX.length);
  return path;
}

export function hasEsPrefix(path: string): boolean {
  return path === ES_PREFIX || path.startsWith(`${ES_PREFIX}/`);
}

// Language-toggle target, computed from the REAL browser path. On /es
// pages the middleware rewrite makes usePathname() report the BARE path
// (/catalog, not /es/catalog), so router.push to the EN URL is a no-op.
// The toggle must therefore read window.location.pathname (the address
// bar) and hard-navigate to this target. Pure + unit-tested so both
// directions and round-trips (EN->ES->EN) are verified without a browser.
//   toES=true  : "/catalog"    -> "/es/catalog" ; "/" -> "/es"
//   toES=false : "/es/catalog" -> "/catalog"    ; "/es" -> "/"
export function languageToggleTarget(browserPath: string, toES: boolean): string {
  const bare = stripEsPrefix(browserPath);
  return toES ? esPath(bare) : bare;
}

// Locale-aware internal link. In ES, prefix absolute in-site paths with
// /es so navigation stays Spanish. Leaves EN untouched, and never
// touches external URLs, mailto:, tel:, anchors, or already-prefixed
// /es paths.
export function localeHref(href: string, locale: Locale): string {
  if (locale !== "es") return href;
  if (!href.startsWith("/")) return href; // external, mailto:, tel:, #anchor
  if (hasEsPrefix(href)) return href;
  return esPath(href);
}
